import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState
} from 'react';
import dayjs, { Dayjs } from 'dayjs';
import {
  Typography,
  useMediaQuery,
  useTheme,
  Alert,
  Snackbar
} from '@mui/material';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Download } from 'lucide-react';
import { Button as ShadcnButton } from '../../components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger
} from '../../components/ui/dropdown-menu';
import CustomDateRangePicker from '../../components/Analytics/CustomDateRangePicker/CustomDateRangePicker';
import { Switch } from '../../components/ui/switch';
import PageTitleSection from '../../components/PageTitleSection/PageTitleSection';
import TopQuestionsBarChart from './TopQuestionsBarChart/TopQuestionsBarChart';
import NoDataMessage from './NoDataMessage/NoDataMessage';
import ChatbotContext from '../../context/ChatbotContext/ChatbotContext';
import ChatbotAnalytics from '../../types/Analytics/ChatbotAnalytics';
import AnalyticsService from '../../services/AnalyticsService/AnalyticsService';
import ChatbotUsageDoughnutChart from './ChatbotUsageDoughnutChart/ChatbotUsageDoughnutChart';
import AnalyticsCardsContainer from './AnalyticsCardsContainer/AnalyticsCardsContainer';
import TopLinks from './TopLinks/TopLinks';
import AnalyticsCard from '../../components/Analytics/AnalyticsCard/AnalyticsCard';
import TopicsChart from './TopicsChart/TopicsChart';
import ConversationsChart from './ConversationsChart/ConversationsChart';
import TopicsDoughnutChart from './TopicsDoughnutChart/TopicsDoughnutChart';
import TopicsSharedLegend from './TopicsSharedLegend/TopicsSharedLegend';
import useDateRange from '../../hooks/useDateRange';
import useAbortController from '../../hooks/useAbortController';
import BotStatusContext from '../../context/BotStatusContext/BotStatusContext';
import PageContentContainer from '../../components/PageContentContainer/PageContentContainer';
import CustomTabs from '../../components/CustomTabs/CustomTabs';

function AnalyticsPage(): React.ReactElement {
  const [analyticsDataCurrent, setAnalyticsDataCurrent] =
    useState<ChatbotAnalytics | null>(null);
  const [analyticsDataPrevious, setAnalyticsDataPrevious] =
    useState<ChatbotAnalytics | null>(null);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [selectedTopics, setSelectedTopics] = useState<string[]>([]);

  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'));
  const { dateRange, setDateRange, includeTest, setIncludeTest } =
    useDateRange();
  const [searchParams, setSearchParams] = useSearchParams();

  const { currentChatbot } = useContext(ChatbotContext);

  const [dateState, setDateState] = useState<{
    current: [Dayjs | null, Dayjs | null];
    previous: [Dayjs | null, Dayjs | null];
  }>({
    current: [null, null],
    previous: [null, null]
  });

  const { liveBotVersion } = useContext(BotStatusContext);

  const isUnbuilt = liveBotVersion?.buildState === 'NOT_BUILT';

  const daysInRange = useMemo(() => {
    if (!dateState.current[0] || !dateState.current[1]) {
      return 0;
    }
    return dateState.current[1].diff(dateState.current[0], 'day') + 1;
  }, [dateState.current]);

  const handleChangeDateRange = (
    startOfRange: Dayjs,
    endOfRange: Dayjs
  ): void => {
    if (
      dateState.current[0]?.isSame(startOfRange) &&
      dateState.current[1]?.isSame(endOfRange)
    ) {
      return;
    }

    const startDiff = endOfRange.diff(startOfRange, 'day') + 1;
    const prevStartDate = startOfRange.subtract(startDiff, 'day');
    const prevEndDate = startOfRange.subtract(1, 'day');

    setDateRange([startOfRange, endOfRange]);
    setDateState({
      current: [startOfRange, endOfRange],
      previous: [prevStartDate, prevEndDate]
    });

    setSearchParams((params) => {
      params.set('startDate', startOfRange.format('YYYY-MM-DD'));
      params.set('endDate', endOfRange.format('YYYY-MM-DD'));
      return params;
    });
  };

  useEffect(() => {
    const startDate = searchParams.get('startDate');
    const endDate = searchParams.get('endDate');

    if (startDate && endDate) {
      const newStartDate = dayjs(startDate);
      const newEndDate = dayjs(endDate);
      handleChangeDateRange(newStartDate, newEndDate);
    }
  }, []);

  useEffect(() => {
    if (!dateRange[0] || !dateRange[1]) return;

    if (
      !dateState.current[0]?.isSame(dateRange[0]) ||
      !dateState.current[1]?.isSame(dateRange[1])
    ) {
      const startDiff = dateRange[1].diff(dateRange[0], 'day') + 1;
      const prevStartDate = dateRange[0].subtract(startDiff, 'day');
      const prevEndDate = dateRange[0].subtract(1, 'day');

      setDateState({
        current: [dateRange[0], dateRange[1]],
        previous: [prevStartDate, prevEndDate]
      });
    }
  }, [dateRange]);

  const parsedDates = useMemo(() => {
    const currentStart = dateState.current[0]?.format('YYYY-MM-DD');
    const currentEnd = dateState.current[1]?.format('YYYY-MM-DD');
    const prevStart = dateState.previous[0]?.format('YYYY-MM-DD');
    const prevEnd = dateState.previous[1]?.format('YYYY-MM-DD');

    if (!currentStart || !currentEnd || !prevStart || !prevEnd) {
      return {
        currentStart: '',
        currentEnd: '',
        prevStart: '',
        prevEnd: ''
      };
    }

    return {
      currentStart,
      currentEnd,
      prevStart,
      prevEnd
    };
  }, [
    dateState.current[0]?.format('YYYY-MM-DD'),
    dateState.current[1]?.format('YYYY-MM-DD'),
    dateState.previous[0]?.format('YYYY-MM-DD'),
    dateState.previous[1]?.format('YYYY-MM-DD')
  ]);

  const { getAbortSignal } = useAbortController();

  const handleFetchAnalyticsData = useCallback((): void => {
    if (
      !currentChatbot ||
      !parsedDates.currentStart ||
      !parsedDates.currentEnd ||
      !parsedDates.prevStart ||
      !parsedDates.prevEnd
    ) {
      return;
    }

    const signal = getAbortSignal();

    setIsLoading(true);
    setErrorMessage('');

    Promise.all([
      AnalyticsService.getAnalytics(
        currentChatbot.id,
        parsedDates.currentStart,
        parsedDates.currentEnd,
        signal,
        includeTest
      ),
      AnalyticsService.getAnalytics(
        currentChatbot.id,
        parsedDates.prevStart,
        parsedDates.prevEnd,
        signal,
        includeTest
      )
    ])
      .then(([dataCurrent, dataPrevious]) => {
        setAnalyticsDataCurrent(dataCurrent);
        setAnalyticsDataPrevious(dataPrevious);
      })
      .catch((err) => {
        if (axios.isCancel(err)) return;

        const error =
          err.response?.data?.message ||
          err.message ||
          'Something went wrong. Please try again later.';
        setErrorMessage(error);
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [currentChatbot, parsedDates, getAbortSignal, includeTest]);

  useEffect(() => {
    handleFetchAnalyticsData();
  }, [handleFetchAnalyticsData]);

  useEffect(() => {
    if (analyticsDataCurrent?.totalCategories?.categories) {
      const sortedTopics = [...analyticsDataCurrent.totalCategories.categories]
        .filter((cat) => cat.category.toLowerCase() !== 'missed utterance')
        .sort((a, b) => b.count - a.count)
        .slice(0, 5)
        .map((cat) => cat.category);
      setSelectedTopics(sortedTopics);
    }
  }, [analyticsDataCurrent?.totalCategories?.categories]);

  const [tabValue, setTabValue] = useState('conversations');
  const [hasExportError, setHasExportError] = useState<boolean>(false);
  const [openExportSnackbar, setOpenExportSnackbar] = useState(false);

  const handleCloseExportSnackbar = (): void => {
    setHasExportError(false);
    setOpenExportSnackbar(false);
  };

  const handleToggleTopic = (topic: string): void => {
    setSelectedTopics((prev) => {
      if (prev.includes(topic)) {
        return prev.filter((t) => t !== topic);
      }
      return [...prev, topic];
    });
  };

  const handleExport = (): void => {
    if (currentChatbot) {
      const updatedParams = new URLSearchParams(searchParams.toString());
      if (dateRange[0]) {
        updatedParams.set('startDate', dateRange[0].format('YYYY-MM-DD'));
      }
      if (dateRange[1]) {
        updatedParams.set('endDate', dateRange[1].format('YYYY-MM-DD'));
      }

      AnalyticsService.exportAnalytics(
        currentChatbot.id,
        updatedParams,
        'xlsx'
      ).catch((err) => {
        if (err.name === 'CanceledError') return;
        setHasExportError(true);
        setOpenExportSnackbar(true);
      });
    }
  };

  const handleExportMissedUtteranceReport = (): void => {
    if (!currentChatbot || !dateRange[0] || !dateRange[1]) return;
    AnalyticsService.exportMissedUtteranceReport(
      currentChatbot.id,
      dateRange[0].format('YYYY-MM-DD'),
      dateRange[1].format('YYYY-MM-DD')
    ).catch((err) => {
      if (err.name === 'CanceledError') return;
      setHasExportError(true);
      setOpenExportSnackbar(true);
    });
  };

  return (
    <>
      <PageTitleSection title='Analytics'>
        <CustomDateRangePicker
          dateRange={dateRange}
          handleChangeDateRange={handleChangeDateRange}
          daysInRange={daysInRange}
        />
      </PageTitleSection>
      <PageContentContainer>
        {!errorMessage && (
          <CustomTabs
            rightContent={
              <div className='flex items-center gap-4 pl-3'>
                <div className='flex items-center gap-2'>
                  <Switch
                    checked={includeTest}
                    onCheckedChange={setIncludeTest}
                  />
                  <span className='text-sm text-muted-foreground'>
                    Include test conversations
                  </span>
                </div>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <ShadcnButton variant='outline' size='sm'>
                      <Download size={16} className='mr-2.5' />
                      Export
                    </ShadcnButton>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align='end'>
                    <DropdownMenuLabel>Export Options</DropdownMenuLabel>
                    <DropdownMenuItem onClick={handleExport}>
                      Analytics report
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={handleExportMissedUtteranceReport}
                    >
                      Missed utterance report
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            }
            tabs={[
              {
                label: 'Conversations',
                value: 'conversations',
                content: isUnbuilt ? (
                  <NoDataMessage />
                ) : (
                  <div className='-mt-1 space-y-4'>
                    <AnalyticsCardsContainer
                      currentData={analyticsDataCurrent?.totalConversations}
                      prevData={analyticsDataPrevious?.totalConversations}
                      isLoading={isLoading}
                    />
                    <ConversationsChart
                      dateRange={dateRange}
                      conversationsData={
                        analyticsDataCurrent?.conversationsOverTime
                      }
                      isLoading={isLoading}
                      isSmallScreen={isSmallScreen}
                    />
                  </div>
                )
              },
              {
                label: 'Topics',
                value: 'topics',
                content: isUnbuilt ? (
                  <NoDataMessage />
                ) : (
                  <div className='-mt-1 space-y-4'>
                    <div className='grid grid-cols-1 gap-4 lg:grid-cols-5'>
                      <div className='lg:col-span-2'>
                        <AnalyticsCard
                          isLoading={isLoading}
                          title='Topics distribution'
                          content={
                            analyticsDataCurrent?.totalCategories &&
                            analyticsDataCurrent?.totalCategories.categories
                              .length > 0 ? (
                              <TopicsDoughnutChart
                                topicsData={
                                  analyticsDataCurrent?.totalCategories
                                }
                                isSmallScreen={isSmallScreen}
                                hideLegend
                                selectedGroups={selectedTopics}
                              />
                            ) : (
                              <Typography
                                variant='body2'
                                sx={{ textAlign: 'center', m: 2 }}
                              >
                                No topics have been matched during this date
                                range.
                              </Typography>
                            )
                          }
                          style={{ height: 530 }}
                        />
                      </div>
                      <div className='lg:col-span-3'>
                        <TopicsChart
                          dateRange={dateRange}
                          topicsData={analyticsDataCurrent?.categoriesOverTime}
                          isLoading={isLoading}
                          isSmallScreen={isSmallScreen}
                          hideLegend
                          selectedGroups={selectedTopics}
                        />
                      </div>
                    </div>
                    <TopicsSharedLegend
                      topicsData={analyticsDataCurrent?.totalCategories}
                      selectedGroups={selectedTopics}
                      onToggleGroup={handleToggleTopic}
                    />
                  </div>
                )
              },
              {
                label: 'Performance',
                value: 'performance',
                content: isUnbuilt ? (
                  <NoDataMessage />
                ) : (
                  <div className='grid grid-cols-1 gap-4 xl:grid-cols-2'>
                    <div className='xl:col-span-1'>
                      <AnalyticsCard
                        isLoading={isLoading}
                        title='Top questions'
                        tooltipText='Top questions matched during the selected period'
                        content={
                          analyticsDataCurrent?.top10Questions &&
                          analyticsDataCurrent?.top10Questions.length > 0 ? (
                            <TopQuestionsBarChart
                              questionsData={
                                analyticsDataCurrent?.top10Questions
                              }
                              isSmallScreen={isSmallScreen}
                            />
                          ) : (
                            <Typography
                              variant='body2'
                              sx={{ textAlign: 'center', m: 2 }}
                            >
                              No questions have been matched during this date
                              range.
                            </Typography>
                          )
                        }
                        style={{ height: 580 }}
                      />
                    </div>
                    <div className='xl:col-span-1 grid grid-cols-1 gap-4'>
                      <AnalyticsCard
                        isLoading={isLoading}
                        title='Chatbot usage'
                        tooltipText='Chatbot usage by time of day during the selected period'
                        content={
                          analyticsDataCurrent?.totalConversations &&
                          analyticsDataCurrent?.totalConversations
                            .totalConversations > 0 ? (
                            <ChatbotUsageDoughnutChart
                              conversationsData={
                                analyticsDataCurrent?.totalConversations
                              }
                              isLoading={isLoading}
                              isSmallScreen={isSmallScreen}
                            />
                          ) : (
                            <Typography
                              variant='body2'
                              sx={{ textAlign: 'center', m: 2 }}
                            >
                              No chatbot usage during this date range.
                            </Typography>
                          )
                        }
                        style={{ height: 280 }}
                      />
                      <AnalyticsCard
                        isLoading={isLoading}
                        title='Top links'
                        tooltipText='Links with the most clicks during the selected period'
                        content={
                          <TopLinks
                            linksData={analyticsDataCurrent?.top10LinkClicks}
                            isSmallScreen={isSmallScreen}
                          />
                        }
                        style={{ height: 280 }}
                        contentClassName='overflow-hidden'
                      />
                    </div>
                  </div>
                )
              }
            ]}
            defaultValue='conversations'
            value={tabValue}
            onValueChange={setTabValue}
          />
        )}
      </PageContentContainer>

      {hasExportError && (
        <Snackbar
          open={openExportSnackbar}
          autoHideDuration={6000}
          onClose={handleCloseExportSnackbar}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        >
          <Alert onClose={handleCloseExportSnackbar} severity='error'>
            Failed to export. Please try again later.
          </Alert>
        </Snackbar>
      )}
    </>
  );
}

export default AnalyticsPage;
